/**
 * Profile step - collects first and last name and calls updateProfile.
 *
 * Rendered by SetupFlow when the current setupStep is 'profile'. On success,
 * invokes `onComplete` so the orchestrator can advance to the next step (or
 * unmount when no steps remain).
 *
 * Error handling:
 * - Surfaces the thrown error message in red text.
 * - updateProfile itself captures to PostHog; this component does not.
 *
 * @module components/setup/ProfileStep
 */

import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useOrg } from '../../lib/OrgProvider';
import { useSupabase } from '../../lib/SupabaseProvider';
import { captureEvent } from '../../lib/telemetry/posthog/frontend';
import { ButtonContent } from '../ui/ButtonContent';

interface ProfileStepProps {
  /** Called after updateProfile resolves successfully. */
  onComplete?: () => void;
}

/**
 * Try to extract first/last name from an email address.
 * Handles common patterns: "jane.doe@", "jane_doe@", "janedoe@".
 */
function guessNameFromEmail(email: string): { first: string; last: string } {
  const local = email.split('@')[0] ?? '';
  const cleaned = local.split('+')[0];
  const parts = cleaned.split(/[._-]/).filter(Boolean);
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  if (parts.length >= 2) return { first: cap(parts[0]), last: cap(parts[parts.length - 1]) };
  if (parts.length === 1) return { first: cap(parts[0]), last: '' };
  return { first: '', last: '' };
}

/** Form collecting the user's first and last name. */
export const ProfileStep: React.FC<ProfileStepProps> = ({ onComplete }) => {
  const { updateProfile } = useOrg();
  const { user } = useSupabase();

  const guessed = user?.email ? guessNameFromEmail(user.email) : { first: '', last: '' };
  const [firstName, setFirstName] = useState(guessed.first);
  const [lastName, setLastName] = useState(guessed.last);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const didPrefill = useRef(false);

  useEffect(() => {
    if (didPrefill.current) return;
    if (user?.email) {
      const g = guessNameFromEmail(user.email);
      setFirstName((prev) => (prev === '' ? g.first : prev));
      setLastName((prev) => (prev === '' ? g.last : prev));
      didPrefill.current = true;
    }
  }, [user?.email]);

  const isValid = firstName.trim() !== '' && lastName.trim() !== '';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setError(null);
    setSubmitting(true);
    try {
      await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      captureEvent('onboarding.profile_created');
      onComplete?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn\'t save your name. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-foreground">What should we call you?</h1>
        <p className="text-muted-foreground text-sm mt-1">This is how collaborators will see you.</p>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <label htmlFor="firstName" className="block text-sm font-medium text-muted-foreground mb-1.5">
            First name
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Jane"
            autoFocus
            className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-volt"
          />
        </div>

        <div className="flex-1">
          <label htmlFor="lastName" className="block text-sm font-medium text-muted-foreground mb-1.5">
            Last name
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:border-volt"
          />
        </div>
      </div>

      {error && <p className="text-danger text-sm">{error}</p>}

      <div className="flex-1" />
      <button
        type="submit"
        disabled={!isValid || submitting}
        aria-busy={submitting}
        className="w-full rounded-lg px-4 py-2.5 button-accent font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ButtonContent loading={submitting}>Continue</ButtonContent>
      </button>
    </form>
  );
};
